import { gql } from "@apollo/client";
import TimeTableInput from "./timeTableInput";
import { useQuery, useMutation} from "@apollo/client";
import React, { useEffect, useState, useRef } from "react";
import { formatDateForApi, parseTimeStringToMinutes, isUnitNow as isUnitNowUtil, getCurrentTimeISO, isElementFocusedWithin } from "../Utils/timeutils";
import { buildStudentHref } from "../Utils/utils";

const GET_TIME_UNITS_QUERY = gql`
  query GetTimeUnits {
    timeUnitsPlan {
      index
      startTime
      endTime
    }
  }
`;

const GET_DAY_QUERY = gql`
  query GetClassDayTable($classId: String!, $date: String!) {
    getClassDayTable(classId: $classId, date: $date) {
      date
      weekNumber
      dayOfWeek
      dayName
      dayShortName
      holiday {
        name
      }
      unitCells {
        weekNumber
        dayOfWeek
        unitIndex
        Editable
        Content
      }
      student {
        id
        username
        surname
        lastname
        boarding
      }
    }
  }
`;

const SET_STUDENT_UNIT_MUTATION = gql`
  mutation SetStudentUnit($id: String!, $weekNumber: Int!, $dayNumber: Int!, $unitNumber: Int!, $content: String!) {
    setStudentUnit(studentId: $id, weekNumber: $weekNumber, dayNumber: $dayNumber, unitNumber: $unitNumber, content: $content)
  }
`;

const GET_SCHOOL_YEAR_OVERVIEW = gql`
  query GetSchoolYearOverview {
    schoolYearOverview {
      normalStudentUnits
    }
  }
`;

export default function DayTimetable({ selectedClass, date, weekIndex, dayIndex, fullView }) {
  // Format date to string in local timezone or default to central European timezone
  const formattedDate = formatDateForApi(date);

  // State to force re-render when data updates
  // Removed unused state variable 'dataUpdated'

  // Query for time units
  const { data: timeUnitsData, loading: timeUnitsLoading, error: timeUnitsError } = 
    useQuery(GET_TIME_UNITS_QUERY);

  // Query for class day table
  const { data: dayData, loading: dayLoading, error: dayError, refetch: refetchDayData } = 
    useQuery(GET_DAY_QUERY, {
      variables: { classId: selectedClass, date: formattedDate },
      skip: !selectedClass || !formattedDate,
      onCompleted: () => {
        // Data has been updated, trigger re-render if needed
        // setDataUpdated(prev => !prev); --- IGNORE ---
      }
    });

  // Query for school year overview
  const { data: schoolYearData, loading: schoolYearLoading, error: schoolYearError } = 
    useQuery(GET_SCHOOL_YEAR_OVERVIEW);

  const normalStudentUnits = schoolYearData?.schoolYearOverview?.normalStudentUnits || 0;

  // Mutation for setting student unit
  const [setStudentUnitMutation] = useMutation(SET_STUDENT_UNIT_MUTATION);

  // State for storing limited data for rendering
  const [limitedData, setLimitedData] = useState({ timeUnits: [], unitCells: [] });

  // Refetch data when `selectedClass` or `date` changes
  React.useEffect(() => {
    if (selectedClass && formattedDate) {
      refetchDayData({ classId: selectedClass, date: formattedDate });
    }
  }, [selectedClass, formattedDate, refetchDayData]);

  // Extract time units and sort by index
  const timeUnits = [...(timeUnitsData?.timeUnitsPlan || [])].sort((a, b) => a.index - b.index);

  // The day data is an array of student-day objects
  const dayInfoArray = dayData?.getClassDayTable || [];

  // Extract all students from the dayInfoArray
  const students = dayInfoArray.map(dayInfo => dayInfo.student).filter(Boolean);

  // Correct 'dayInfo' usage by initializing it properly
  const dayInfo = dayData?.getClassDayTable?.[0] || {};

  // Extract unit cells
  const unitCells = dayInfo?.unitCells || [];

  // Effect to handle fullView prop changes - only run when deps actually change
  React.useEffect(() => {

    // Skip during initial render or when data isn't available
    if (!timeUnits.length || !dayData) {
      return;
    }
    
    if (!fullView) {
      // Limit the number of time units and user time cells to normalStudentUnits
      const limitedTimeUnits = timeUnits.slice(0, normalStudentUnits);
      const limitedUnitCells = unitCells.filter(cell => 
        limitedTimeUnits.some(unit => unit.index === cell.unitIndex)
      );

      setLimitedData({ timeUnits: limitedTimeUnits, unitCells: limitedUnitCells });
    } else {
      setLimitedData({ timeUnits, unitCells });
    }
  }, [fullView, normalStudentUnits, dayData]);

  // Handle cell change — match TimeTableInput onChange signature (oldValue, newValue, timeUnitIndex, dayNumber, studentId)
  const handleCellChange = async (oldValue, newValue, timeUnitIndex, dayNumber, studentId) => {
    try {
      const response = await setStudentUnitMutation({
        variables: {
          id: studentId,
          weekNumber: weekIndex,
          dayNumber: dayNumber,
          unitNumber: timeUnitIndex,
          content: newValue
        }
      });
      
      if (response?.data?.setStudentUnit) {
        await refetchDayData({ classId: selectedClass, date: formattedDate });
      } else {
        console.error("Failed to update cell: No confirmation from server");
      }
    } catch (error) {
      console.error("Error updating cell:", error);
    }
  };

  // Use limitedData for rendering instead of timeUnits and unitCells
  const timeUnitsToRender = limitedData.timeUnits;
  const unitCellsToRender = limitedData.unitCells;

  // Ref for the timetable container so we can detect if any child input is focused
  const containerRef = useRef(null);

  // Current timestamp (updated every minute) used to compute which unit is 'now'
  const [currentTimeISO, setCurrentTimeISO] = useState(() => getCurrentTimeISO());

  // Update currentTimeISO every minute, but skip updates while a timetable input is focused
  useEffect(() => {
    const tick = () => {
      if (isElementFocusedWithin(containerRef)) {
        // An input inside the timetable is focused -> do not change the 'now' markers
        return;
      }
      setCurrentTimeISO(getCurrentTimeISO());
    };

    // Run immediately and then every 60s
    tick();
    const timerId = setInterval(tick, 60000);
    return () => clearInterval(timerId);
  }, []);

  // Helper to determine whether a time unit should be marked as 'now'
  const isUnitNow = (unit) => {
    // Use shared util but offset local timezone adjustment removed
    return isUnitNowUtil(unit, new Date(currentTimeISO));
  };

  // Loading state
  if (timeUnitsLoading || dayLoading || schoolYearLoading) {
    return <div className="p-4 text-center">Loading timetable data...</div>;
  }

  // Error state
  if (timeUnitsError || dayError || schoolYearError) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded">
        <div className="font-bold">Error loading timetable:</div>
        <div className="text-red-600">{timeUnitsError?.message || dayError?.message || schoolYearError?.message}</div>
      </div>
    );
  }

  // Missing class/date selection
  if (!selectedClass || !formattedDate) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded text-center">
        Please select a class and date to view the timetable
      </div>
    );
  }

  // If selected day is a holiday, show only the holiday name centered
  if (dayInfo?.holiday) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="text-center">
          <div className="mt-2 text-lg">{dayInfo.holiday.name}</div>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="overflow-x-auto w-full" style={{ borderRight: '1px solid #e5e7eb' }}>
      <table className="table-fixed w-full">
        <thead>
          <tr>
            <th className="sticky left-0 z-20 bg-gray-200 w-40 p-0 text-center align-middle">
              <div className="w-full h-full px-2 py-1">
                <p className="text-gray-500 text-sm truncate">
                  {dayInfo?.dayName || 'Kein Tagesname'}
                </p>
              </div>
            </th>
            {timeUnitsToRender.map(unit => (
              <th
                key={unit.index}
                className={`align-middle w-20 min-w-[80px] max-w-[80px] ${
                  isUnitNow(unit) ? 'nowTimeCell topborder' : ''
                }`}
              >
                <div className="flex flex-col justify-center items-center min-h-[64px] font-semibold text-gray-700 border-b border-gray-300 p-2">
                  <div className="text-xs text-gray-500 mt-1">
                    {unit.startTime} - {unit.endTime}
                  </div>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {students.length > 0 ? (
            students.map(student => (
              <tr key={student.id}>
                <td
                  className={`sticky left-0 z-20 w-40 p-0 text-left whitespace-normal break-words ${
                    student.boarding ? 'bg-yellow-50' : 'bg-white'
                  }`}
                  style={{ borderLeft: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}
                >
                  <div className="w-full h-full px-2 py-1 text-neutral-900">
                    <a
                      href={buildStudentHref(student.id)}
                      className="text-left w-full"
                    >
                      <span className="block uppercase font-bold truncate">{student.lastname || 'Kein Nachname'}</span>
                      <span className="block text-sm truncate">{student.surname || 'Kein Vorname'}</span>
                    </a>
                  </div>
                </td>

                {timeUnitsToRender.map((unit, colIdx) => {
                  const matchingDay = dayInfoArray.find(d => d.student?.id === student.id) || {};
                  const studentUnitCells = matchingDay.unitCells || [];

                  const cellData = studentUnitCells.find(cell => cell.unitIndex === unit.index) || {};
                  const left = colIdx === 0 ? null : studentUnitCells.find(cell => cell.unitIndex === timeUnitsToRender[colIdx - 1].index)?.Content;
                  const right = colIdx === timeUnitsToRender.length - 1 ? null : studentUnitCells.find(cell => cell.unitIndex === timeUnitsToRender[colIdx + 1].index)?.Content;

                  return (
                    <td
                      key={`${student.id}-${unit.index}`}
                      className={`relative h-full p-0 w-20 min-w-[80px] max-w-[80px] ${
                        isUnitNow(unit) ? 'nowTimeCell' : ''
                      }`}
                    >
                      <TimeTableInput
                        data={cellData.Content}
                        editable={cellData.Editable}
                        dataLeft={left}
                        dataRight={right}
                        timeUnitIndex={unit.index}
                        dayIndex={dayIndex}
                        onChange={handleCellChange}
                        studentId={student.id}
                      />
                    </td>
                  );
                })}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={timeUnitsToRender.length + 1} className="text-center p-4 text-gray-500">
                Keine Schülerdaten verfügbar
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}